/**
 * API Client Context
 *
 * Provides a way for projects to inject their generated API client
 * so that framework components can call reducers without depending
 * on project-specific code.
 *
 * This enables framework modules like shadcn-components to call
 * reducers like `User.updatePersonalUserData` without directly
 * importing the project's API client.
 *
 * The generatedClient is typically provided via FrontendProvision.generatedClient
 * and automatically wired up by AtomFrameworkProvider.
 */

import {
  CONTEXT_KEY,
  DefaultEntities,
  type IPrivateUserData,
  type UserPublicID,
} from "@atoms/atom-types";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { AtomStoreContext } from "./AtomStoreProvider";
import type { ApiRequestOptions } from "./apiRequestOptions";
import type { GeneratedApiClient } from "./GeneratedApiClient";
import { useCombinedContext } from "./useCombinedContext";

/**
 * Data required to invite a user.
 * Matches the parameters of Global.inviteUser reducer.
 */
export interface InviteUserData {
  email: string;
  firstName: string;
  lastName: string;
  /**
   * URL to redirect to after magic link verification (for existing users).
   */
  callbackURL: string;
  /**
   * URL to redirect to after magic link verification (for new users, typically onboarding page).
   */
  newUserCallbackURL: string;
  /** Optional functional role keys to assign on invite. */
  permissions?: string[];
  /** Whether the invited user should have administrator privileges. */
  isAdmin?: boolean;
}

/**
 * Context value for the API client.
 */
interface ApiClientContextValue {
  /**
   * The raw generated API client object.
   */
  generatedClient: GeneratedApiClient;
}

const ApiClientContext = createContext<ApiClientContextValue | null>(null);

export interface ApiClientProviderProps {
  children: ReactNode;
  /**
   * The project's generated API client object.
   * This is the raw object with `.handler` functions, not the hooked version.
   */
  generatedClient: GeneratedApiClient;
}

/**
 * Provider for the API client.
 *
 * Wrap your app with this provider (inside AtomStoreProvider) to enable
 * framework components to call reducers.
 */
export function ApiClientProvider({
  children,
  generatedClient,
}: Readonly<ApiClientProviderProps>) {
  const value = useMemo(() => ({ generatedClient }), [generatedClient]);

  return (
    <ApiClientContext.Provider value={value}>
      {children}
    </ApiClientContext.Provider>
  );
}

/**
 * Standard User API interface that framework components can depend on.
 *
 * This is what `useStandardUserApi()` returns.
 */
export interface StandardUserApi {
  /**
   * Partially update personal user data for the user in the current context.
   */
  patchPersonalData: (patch: Partial<IPrivateUserData>) => Promise<void>;

  /**
   * Partially update personal user data for a specific user (admin use).
   */
  patchPersonalDataForUser?: (
    userId: UserPublicID,
    patch: Partial<IPrivateUserData>,
    /** For a caller that shows the failure itself: `{ showErrorToast: false }`. */
    requestOptions?: ApiRequestOptions,
  ) => Promise<void>;

  /**
   * Invite a new user by creating them in the system and sending an invitation email.
   * This is a single transactional operation - if any step fails, the entire operation is rolled back.
   * Returns the created user's ID.
   *
   * This method is only available if the project has a `Global.inviteUser` reducer.
   */
  inviteUser?: (data: InviteUserData) => Promise<UserPublicID>;

  /**
   * Request an email change for the user in the current context.
   * Sends a verification email to the new address (linked auth users).
   *
   * This method is only available if the project has a `User.requestEmailChange` reducer.
   */
  requestEmailChange?: (newEmail: string) => Promise<void>;
}

/**
 * Hook to get the standard User API methods.
 *
 * Returns null if:
 * - ApiClientProvider is not present
 * - AtomStoreProvider is not present
 * - The API client doesn't have a User relation with patchPersonalUserData
 *
 * @example
 * ```tsx
 * const userApi = useStandardUserApi();
 * if (userApi) {
 *   await userApi.patchPersonalData({ firstName: "John" });
 * }
 * ```
 */
export function useStandardUserApi(): StandardUserApi | null {
  const apiClientContext = useContext(ApiClientContext);
  const atomStore = useContext(AtomStoreContext);
  const combinedContext = useCombinedContext();

  return useMemo(() => {
    if (!apiClientContext?.generatedClient || !atomStore) {
      return null;
    }

    const { generatedClient } = apiClientContext;
    const apolloClient = atomStore.getApolloClient();
    const { onError, onSuccess } = atomStore;

    const userRelation = generatedClient.User;
    const patchHandler = userRelation?.patchPersonalUserData?.handler;
    if (!patchHandler) {
      console.warn(
        "[useStandardUserApi] API client does not have User.patchPersonalUserData",
      );
      return null;
    }

    // Check Global reducers (optional)
    const globalRelation = generatedClient.Global;
    const inviteUserHandler = globalRelation?.inviteUser?.handler;
    const requestEmailChangeHandler = userRelation?.requestEmailChange?.handler;

    const api: StandardUserApi = {
      patchPersonalData: async (
        patch: Partial<IPrivateUserData>,
      ): Promise<void> => {
        await patchHandler(
          apolloClient,
          onSuccess,
          onError,
          combinedContext,
          patch,
        );
      },
      patchPersonalDataForUser: async (
        userId: UserPublicID,
        patch: Partial<IPrivateUserData>,
        requestOptions?: ApiRequestOptions,
      ): Promise<void> => {
        await patchHandler(
          apolloClient,
          onSuccess,
          onError,
          {
            ...combinedContext,
            [CONTEXT_KEY(DefaultEntities.User)]: userId,
          },
          patch,
          requestOptions,
        );
      },
    };

    // Add inviteUser if available
    if (inviteUserHandler) {
      api.inviteUser = async (data: InviteUserData): Promise<UserPublicID> => {
        return inviteUserHandler(
          apolloClient,
          onSuccess,
          onError,
          combinedContext,
          data.email,
          data.firstName,
          data.lastName,
          data.callbackURL,
          data.newUserCallbackURL,
          data.permissions ?? [],
          data.isAdmin ?? false,
        );
      };
    }

    if (requestEmailChangeHandler) {
      api.requestEmailChange = async (newEmail: string): Promise<void> => {
        await requestEmailChangeHandler(
          apolloClient,
          onSuccess,
          onError,
          combinedContext,
          newEmail,
        );
      };
    }

    return api;
  }, [apiClientContext, atomStore, combinedContext]);
}

/**
 * Avatar upload/delete handlers for auth UIs. They write the `UserAvatar` file
 * atom (User.uploadAvatar / User.clearAvatar); `upload` resolves to the public
 * URL of the stored file.
 */
export function useAvatarStorage(): {
  upload: (file: File) => Promise<string>;
  delete: (image: string) => Promise<void>;
} | null {
  const apiClientContext = useContext(ApiClientContext);
  const atomStore = useContext(AtomStoreContext);
  const combinedContext = useCombinedContext();

  const userRelation = apiClientContext?.generatedClient?.User;

  const upload = useCallback(
    async (file: File): Promise<string> => {
      if (!apiClientContext?.generatedClient || !atomStore) {
        throw new Error("Avatar upload is not configured for this project");
      }

      const uploadHandler = userRelation?.uploadAvatar?.handler;
      if (!uploadHandler) {
        throw new Error("Avatar upload is not configured for this project");
      }

      const stored = await uploadHandler(
        atomStore.getApolloClient(),
        atomStore.onSuccess,
        atomStore.onError,
        combinedContext,
        file,
      );
      if (!stored.url) {
        throw new Error("The avatar bucket is not readable by URL");
      }
      return stored.url;
    },
    [apiClientContext, atomStore, combinedContext, userRelation],
  );

  const deleteStoredAvatar = useCallback(
    async (_image: string): Promise<void> => {
      const clearHandler = userRelation?.clearAvatar?.handler;
      if (!apiClientContext?.generatedClient || !atomStore || !clearHandler) {
        return;
      }

      await clearHandler(
        atomStore.getApolloClient(),
        atomStore.onSuccess,
        atomStore.onError,
        combinedContext,
      );
    },
    [apiClientContext, atomStore, combinedContext, userRelation],
  );

  return useMemo(() => {
    if (!userRelation?.uploadAvatar?.handler) {
      return null;
    }

    return {
      upload,
      delete: deleteStoredAvatar,
    };
  }, [deleteStoredAvatar, upload, userRelation]);
}
